import { api, apiPublic } from './client';
import type {
  Event,
  EventRsvp,
  EventRsvpStatus,
  EventRsvpSummary,
  EventSeriesMutateResponse,
} from '../features/events/types';
import type { RecurrenceRule } from '../shared/types';
export type {
  Event,
  EventRsvp,
  EventRsvpStatus,
  EventRsvpSummary,
  EventSeriesMutateResponse,
} from '../features/events/types';

export interface EventSeriesConfig {
  id: string;
  startDate: string; // YYYY-MM-DD
  timeStart: string; // HH:mm
  timeEnd: string; // HH:mm
  endDate?: string | null;
  endedAt?: string | null;
  isActive?: boolean;
  recurrence: RecurrenceRule;
  createdAt: string;
  updatedAt: string;
}

export async function getPublicEvents(): Promise<Event[]> {
  return api.get<Event[]>('/api/events/public');
}

export async function getEvents(): Promise<Event[]> {
  return api.get<Event[]>('/api/events');
}

export async function getEvent(id: string): Promise<Event | null> {
  try {
    return await api.get<Event>(`/api/events/${id}`);
  } catch {
    return null;
  }
}

export async function getEventSeriesForEvent(eventId: string): Promise<EventSeriesConfig | null> {
  return api.get<EventSeriesConfig | null>(`/api/events/${eventId}/series`);
}

export async function getPublicEventSeriesForEvent(eventId: string): Promise<EventSeriesConfig | null> {
  return apiPublic.get<EventSeriesConfig | null>(`/api/events/${eventId}/public-series`);
}

/** Public endpoint – no auth. Use for guest RSVP summary on home page. */
export async function getEventRsvpSummary(eventId: string): Promise<EventRsvpSummary> {
  return apiPublic.get<EventRsvpSummary>(`/api/events/${eventId}/rsvp-summary`);
}

/** Public (guest) RSVP – no auth. Pass guestToken for max-5-per-event limit. On 409, error is EVENT_FULL | ALREADY_REGISTERED | MAX_RSVPS_REACHED. */
export async function submitPublicEventRsvp(
  eventId: string,
  data: { name: string; email?: string | null; phone?: string | null; occurrenceDate?: string | null },
  guestToken?: string | null
): Promise<EventRsvp> {
  return apiPublic.post<EventRsvp>(`/api/events/${eventId}/rsvp`, { ...data, guestToken: guestToken ?? undefined });
}

export async function endEventSeries(eventId: string, endDate: string): Promise<{ id: string; endDate: string | null }> {
  return api.post<{ id: string; endDate: string | null }>(`/api/events/${eventId}/series/end`, { endDate });
}

export async function createEvent(data: {
  name: string;
  description?: string;
  dtDateOfEvent: string;
  dtTimeStartOfEvent: string;
  dtTimeEndOfEvent: string;
  recurrence?: RecurrenceRule;
  isAPaidEvent?: boolean;
  priceOfEntry?: number;
  isPublic?: boolean;
  status?: Event['status'];
  capacity?: number | null;
  externalRsvpUrl?: string | null;
  imageId?: string | null;
  isRsvpOnly?: boolean;
  socialLinksJson?: string | null;
}): Promise<Event | EventSeriesMutateResponse> {
  return api.post<Event | EventSeriesMutateResponse>('/api/events', data);
}

export async function updateEvent(
  id: string,
  data: Partial<{
    name: string;
    description: string;
    dtDateOfEvent: string;
    dtTimeStartOfEvent: string;
    dtTimeEndOfEvent: string;
    recurrence: RecurrenceRule;
    updateSeries: boolean;
    isAPaidEvent: boolean;
    priceOfEntry: number;
    isPublic: boolean;
    status: Event['status'];
    capacity: number | null;
    externalRsvpUrl: string | null;
    imageId: string | null;
    isRsvpOnly: boolean;
    socialLinksJson: string | null;
  }>
): Promise<Event | EventSeriesMutateResponse> {
  return api.patch<Event | EventSeriesMutateResponse>(`/api/events/${id}`, data);
}

export async function deleteEvent(id: string): Promise<void> {
  return api.delete(`/api/events/${id}`);
}

export async function getEventRsvps(eventId: string): Promise<EventRsvp[]> {
  return api.get<EventRsvp[]>(`/api/events/${eventId}/rsvps`);
}

export async function createEventRsvp(
  eventId: string,
  data: { name: string; email?: string | null; phone?: string | null; status?: EventRsvpStatus }
): Promise<EventRsvp> {
  return api.post<EventRsvp>(`/api/events/${eventId}/rsvps`, data);
}

export async function updateEventRsvp(
  eventId: string,
  rsvpId: string,
  data: Partial<{ name: string; email: string | null; phone: string | null; status: EventRsvpStatus }>
): Promise<EventRsvp> {
  return api.patch<EventRsvp>(`/api/events/${eventId}/rsvps/${rsvpId}`, data);
}

export async function deleteEventRsvp(eventId: string, rsvpId: string): Promise<void> {
  return api.delete(`/api/events/${eventId}/rsvps/${rsvpId}`);
}

export function isEventUpcoming(date: string | Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime() >= today.getTime();
}

export function isEventPast(date: string | Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

export function formatEventDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-ZA', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatEventTime(date: string | Date): string {
  if (typeof date === 'string') {
    const directTimeMatch = date.match(/^(\d{2}):(\d{2})/);
    const isoTimeMatch = date.match(/T(\d{2}):(\d{2})/);
    const match = directTimeMatch ?? isoTimeMatch;

    if (match) {
      const hour24 = Number(match[1]);
      const minutes = match[2];
      const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
      const period = hour24 >= 12 ? 'PM' : 'AM';
      return `${hour12.toString().padStart(2, '0')}:${minutes} ${period}`;
    }
  }

  const parsed = new Date(date);
  const hour24 = parsed.getUTCHours();
  const minutes = parsed.getUTCMinutes().toString().padStart(2, '0');
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const period = hour24 >= 12 ? 'PM' : 'AM';
  return `${hour12.toString().padStart(2, '0')}:${minutes} ${period}`;
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount);
}
